/**
 * ResponsePage.js
 * ================
 * Gaze-driven response tree navigation screen. Renders either a 3-block or
 * 2-block layout depending on selectionMode, and confirms selections via
 * long-gaze or double-blink based on confirmationMode (both from GazeConfigContext).
 *
 * Layout modes
 * ─────────────
 * '3-blocks'  — teal BACK bar (top) + left / right / bottom blocks
 * '2-blocks'  — teal BACK bar (top) + left / right only
 *
 * Gaze directions
 * ────────────────
 * 'up'    → back (immediate, no confirmation needed in either mode)
 * 'left'  → children[0]
 * 'right' → children[1]
 * 'down'  → children[2] (3-blocks mode only)
 *
 * Confirmation modes
 * ───────────────────
 * 'long-gaze'    — gaze threshold navigates immediately.
 * 'double-blink' — gaze threshold sets pendingNode (green); double blink confirms;
 *                  'up' cancels; 45 s auto-cancel.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChildren } from './responseData';
import { getBinaryChildren } from './responseBinaryData';
import { useGazeNavigation } from './useGazeNavigation';
import { useGazeConfig, COLOR_MAP } from './GazeConfigContext';
import EditableQuadLabel from './EditableQuadLabel';
import MicListenModal from './MicListenModal';
import { speakSegments } from './speakUtils';
import useReadAloud from './useReadAloud';
import './QuadrantPage.css';

const CONFIRM_TIMEOUT_MS = 45_000;
const LONG_GAZE_FEEDBACK_MS = 400;

export default function ResponsePage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { config, windowSize, calibration, labelOverrides, setLabelOverride } = useGazeConfig();
  const isBinary   = config.selectionMode === '2-blocks';
  const isLongGaze = config.confirmationMode === 'long-gaze';

  const colorVars = {
    '--q-bg':   COLOR_MAP[config.backgroundColor] ?? '#ffffff',
    '--q-text': COLOR_MAP[config.textColor]        ?? '#000000',
  };

  const parentId = Number(id);

  // Load children from the correct response tree.
  // 3-blocks: sorted left[0], right[1], bottom[2]
  // 2-blocks: sorted left[0], right[1]
  const children = isBinary
    ? getBinaryChildren(parentId)
    : getChildren(parentId);

  // ── Auto-announce options via TTS when readAloud is on ───────────────────
  const readAloudSegments = isBinary ? [
    children[0] ? ['Look left for',  children[0].label] : null,
    children[1] ? ['Look right for', children[1].label] : null,
    ['Look up to go back'],
  ].filter(Boolean) : [
    children[0] ? ['Look left for',  children[0].label] : null,
    children[1] ? ['Look right for', children[1].label] : null,
    children[2] ? ['Look down for',  children[2].label] : null,
    ['Look up to go back'],
  ].filter(Boolean);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useReadAloud(readAloudSegments, [id, isBinary]);

  // ── Speak the selected label, interrupting any auto-announce ─────────────
  function speakLabel(label) {
    if (!config.readAloud || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(label);
    u.rate = 0.82; u.pitch = 1.0; u.volume = 1.0;
    if (config.voiceURI) {
      const voice = window.speechSynthesis.getVoices().find(v => v.voiceURI === config.voiceURI);
      if (voice) u.voice = voice;
    }
    window.speechSynthesis.speak(u);
  }

  // ── Double-blink pending state ────────────────────────────────────────────
  const [showMicModal, setShowMicModal] = useState(false);
  const [pendingNode, setPendingNode]   = useState(null);
  const [leafConfirmed, setLeafConfirmed] = useState(false);
  const pendingNodeRef  = useRef(null);
  const pendingTimerRef = useRef(null);

  useEffect(() => { pendingNodeRef.current = pendingNode; }, [pendingNode]);

  function clearPending() {
    setPendingNode(null);
    pendingNodeRef.current = null;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }

  useEffect(() => () => clearTimeout(pendingTimerRef.current), []);

  // ── Handle selection of a child node ─────────────────────────────────────
  function handleSelect(child) {
    if (pendingNodeRef.current !== null) return; // already waiting

    speakLabel(child.label);

    // Always set pendingNode so the block flashes green as visual feedback.
    setPendingNode(child);
    pendingNodeRef.current = child;

    if (isLongGaze) {
      // Long-gaze mode: show the green highlight briefly, then navigate.
      pendingTimerRef.current = setTimeout(() => {
        if (child.isFreeResponse) {
          // isFreeResponse leaf: open the custom options screen.
          clearPending();
          navigate(`/free-response/${child.id}`);
        } else if (!child.isLeaf) {
          clearPending();
          navigate(`/response/${child.id}`);
        } else {
          // Plain leaf: block stays green — stop gaze tracking UI.
          setLeafConfirmed(true);
        }
      }, LONG_GAZE_FEEDBACK_MS);
    } else {
      // Double-blink mode: wait for user confirmation — auto-cancel after 45 s.
      pendingTimerRef.current = setTimeout(clearPending, CONFIRM_TIMEOUT_MS);
    }
  }

  // ── Gaze navigation hook ──────────────────────────────────────────────────
  const { videoRef, activeDirection, doubleBlinkCount } = useGazeNavigation({
    enabled: true,
    windowSize,
    calibration,
    onGazeSelect: (direction) => {
      if (!isLongGaze && pendingNodeRef.current !== null) {
        // Double-blink pending: only 'up' cancels.
        if (direction === 'up') clearPending();
        return;
      }

      // 'up' always navigates back immediately (no confirmation in either mode).
      if (direction === 'up') {
        navigate(-1);
        return;
      }

      if (isBinary) {
        // Binary: left → children[0], right → children[1]
        const binMap = { left: 0, right: 1 };
        const idx = binMap[direction];
        if (idx !== undefined && children[idx]) handleSelect(children[idx]);
      } else {
        // 3-blocks: left[0], right[1], down[2]
        const dirToIdx = { left: 0, right: 1, down: 2 };
        const idx = dirToIdx[direction];
        if (idx !== undefined && children[idx]) handleSelect(children[idx]);
      }
    },
  });

  // ── Double-blink confirmation ─────────────────────────────────────────────
  useEffect(() => {
    if (doubleBlinkCount === 0) return;
    const child = pendingNodeRef.current;
    if (child === null) return;
    if (child.isFreeResponse) {
      clearPending();
      navigate(`/free-response/${child.id}`);
    } else if (!child.isLeaf) {
      clearPending();
      navigate(`/response/${child.id}`);
    } else {
      // Plain leaf: block stays green — stop gaze tracking UI.
      setLeafConfirmed(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doubleBlinkCount]);

  // ── On-demand read aloud ──────────────────────────────────────────────────
  const [l, r, b] = children;
  function speakPage() {
    speakSegments([
      l               ? ['Look left for',  l.label] : null,
      r               ? ['Look right for', r.label] : null,
      !isBinary && b  ? ['Look down for',  b.label] : null,
      ['Look up to go back'],
    ].filter(Boolean), config.voiceURI);
  }

  // ── Swipe-up → back to topics ────────────────────────────────────────────
  const touchStartY = useRef(null);

  function onTouchStart(e) {
    touchStartY.current = e.touches[0].clientY;
  }

  function onTouchEnd(e) {
    if (touchStartY.current === null) return;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    if (dy > 80) navigate('/home');
    touchStartY.current = null;
  }

  // ── CSS helpers ───────────────────────────────────────────────────────────

  const blockClass = (child) => {
    if (pendingNode === null) return '';
    return child.id === pendingNode.id ? 'quad-selected' : 'quad-dimmed';
  };

  const gazeClass = (direction) => {
    if (leafConfirmed) return '';
    if (!isLongGaze && pendingNode !== null) return '';
    return activeDirection === direction ? 'quad-active' : '';
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const [left, right, bottom] = children;

  if (isBinary) {
    // ── 2-block layout ──────────────────────────────────────────────────────
    return (
      <>
      {showMicModal && <MicListenModal onClose={() => setShowMicModal(false)} />}
      <div className="quad-page" style={colorVars} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <video ref={videoRef} playsInline muted
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
        <div className="quad-card quad-card--binary">
          {/* Teal BACK bar — gaze 'up' navigates back immediately */}
          <div className={`quad-top quad-back ${gazeClass('up')}`}>
            <button className="quad-back-btn" onClick={() => navigate(-1)}>← BACK</button>
            <button className="quad-speak-btn" onClick={e => { e.stopPropagation(); speakPage(); }} aria-label="Read options aloud">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
            </button>
            <button className="quad-mic-btn" onClick={e => { e.stopPropagation(); setShowMicModal(true); }} aria-label="Open microphone">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="2" width="6" height="11" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0" />
                <line x1="12" y1="17" x2="12" y2="21" />
                <line x1="9" y1="21" x2="15" y2="21" />
              </svg>
            </button>
          </div>
          <div className="quad-middle">
            <div
              className={`quad-left ${left ? blockClass(left) : ''} ${gazeClass('left')}`}
              onClick={() => left && handleSelect(left)}
            >
              {left && <EditableQuadLabel nodeId={left.id} defaultLabel={left.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
            </div>
            <div
              className={`quad-right ${right ? blockClass(right) : ''} ${gazeClass('right')}`}
              onClick={() => right && handleSelect(right)}
            >
              {right && <EditableQuadLabel nodeId={right.id} defaultLabel={right.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // ── 3-block (quad) layout ─────────────────────────────────────────────────
  return (
    <>
    {showMicModal && <MicListenModal onClose={() => setShowMicModal(false)} />}
    <div className="quad-page" style={colorVars} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <video ref={videoRef} playsInline muted
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
      <div className="quad-card">
        {/* Teal BACK bar */}
        <div className={`quad-top quad-back ${gazeClass('up')}`}>
          <button className="quad-back-btn" onClick={() => navigate(-1)}>← BACK</button>
          <button className="quad-speak-btn" onClick={e => { e.stopPropagation(); speakPage(); }} aria-label="Read options aloud">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          </button>
          <button className="quad-mic-btn" onClick={e => { e.stopPropagation(); setShowMicModal(true); }} aria-label="Open microphone">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="2" width="6" height="11" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <line x1="12" y1="17" x2="12" y2="21" />
              <line x1="9" y1="21" x2="15" y2="21" />
            </svg>
          </button>
        </div>
        <div className="quad-middle">
          <div
            className={`quad-left ${left ? blockClass(left) : ''} ${gazeClass('left')}`}
            onClick={() => left && handleSelect(left)}
          >
            {left && <EditableQuadLabel nodeId={left.id} defaultLabel={left.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
          </div>
          <div
            className={`quad-right ${right ? blockClass(right) : ''} ${gazeClass('right')}`}
            onClick={() => right && handleSelect(right)}
          >
            {right && <EditableQuadLabel nodeId={right.id} defaultLabel={right.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
          </div>
        </div>
        <div
          className={`quad-bottom ${bottom ? blockClass(bottom) : ''} ${gazeClass('down')}`}
          onClick={() => bottom && handleSelect(bottom)}
        >
          {bottom && <EditableQuadLabel nodeId={bottom.id} defaultLabel={bottom.label} labelOverrides={labelOverrides} setLabelOverride={setLabelOverride} />}
        </div>
      </div>
    </div>
    </>
  );
}
